import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Zone } from '../../types/production.types';

interface CropGpsPoint {
  id: string;
  variety_name: string;
  variety_species: string;
  plot_code: string;
  gps_coordinates: any;
}

interface Props {
  zones: Zone[];
  cropPoints?: CropGpsPoint[];
}

/**
 * Carte satellite du parcellaire (Leaflet + imagerie Esri).
 * Affiche les zones géolocalisées : contour (polygon) si disponible, sinon
 * marqueur au centre (lat/lng). Les points relevés au GSM (gps.points)
 * apparaissent en petits points violets.
 */
export function ParcelMap({ zones, cropPoints }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [50.404, 3.7502],
      zoom: 17,
      scrollWheelZoom: false,
    });

    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        maxZoom: 19,
        attribution:
          'Imagerie © Esri — Source: Esri, Maxar, Earthstar Geographics',
      },
    ).addTo(map);

    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();
    const bounds: L.LatLngExpression[] = [];

    for (const zone of zones) {
      const gps = zone.gps_coordinates;
      if (!gps || typeof gps !== 'object') continue;

      const label = `<strong>${zone.code}</strong> — ${zone.name}${
        gps.approximate ? '<br/><em>position approximative — à recaler au GSM</em>' : ''
      }`;

      // Contour (polygone)
      if (Array.isArray(gps.polygon) && gps.polygon.length >= 3) {
        const latlngs = gps.polygon
          .filter((p: any) => Array.isArray(p) && p.length >= 2)
          .map((p: any) => [p[0], p[1]] as [number, number]);
        if (latlngs.length >= 3) {
          L.polygon(latlngs, {
            color: gps.approximate ? '#c9a04e' : '#2d6a4f',
            weight: 2,
            fillOpacity: 0.15,
            dashArray: gps.approximate ? '6 4' : undefined,
          })
            .bindPopup(label)
            .addTo(layer);
          latlngs.forEach((ll: [number, number]) => bounds.push(ll));
        }
      }

      // Contours individuels (emplacements de serres S1-S8, tunnels SN1/SN2…)
      if (Array.isArray(gps.sub_polygons)) {
        for (const sub of gps.sub_polygons) {
          if (!Array.isArray(sub?.polygon) || sub.polygon.length < 3) continue;
          const latlngs = sub.polygon
            .filter((p: any) => Array.isArray(p) && p.length >= 2)
            .map((p: any) => [p[0], p[1]] as [number, number]);
          if (latlngs.length < 3) continue;
          L.polygon(latlngs, {
            color: '#2d6a4f',
            weight: 1.5,
            fillColor: '#7fae8e',
            fillOpacity: 0.25,
          })
            .bindTooltip(sub.label || '', { direction: 'center', className: 'parcel-sub-label' })
            .bindPopup(`<strong>${sub.label}</strong> — ${zone.name}`)
            .addTo(layer);
          latlngs.forEach((ll: [number, number]) => bounds.push(ll));
        }
      }

      // Ligne d'arbres (saules têtards…)
      if (Array.isArray(gps.line) && gps.line.length >= 2) {
        const latlngs = gps.line
          .filter((p: any) => Array.isArray(p) && p.length >= 2)
          .map((p: any) => [p[0], p[1]] as [number, number]);
        L.polyline(latlngs, { color: '#2d6a4f', weight: 3, dashArray: '2 6' })
          .bindPopup(label)
          .addTo(layer);
        latlngs.forEach((ll: [number, number]) => bounds.push(ll));
      }

      // Centre (marqueur)
      if (typeof gps.lat === 'number' && typeof gps.lng === 'number') {
        L.circleMarker([gps.lat, gps.lng], {
          radius: 7,
          color: '#fff',
          weight: 2,
          fillColor: gps.approximate ? '#c9a04e' : '#2d6a4f',
          fillOpacity: 0.95,
        })
          .bindPopup(label)
          .addTo(layer);
        bounds.push([gps.lat, gps.lng]);
      }

      // Points relevés au GSM
      if (Array.isArray(gps.points)) {
        for (const pt of gps.points) {
          if (typeof pt?.lat !== 'number' || typeof pt?.lng !== 'number') continue;
          L.circleMarker([pt.lat, pt.lng], {
            radius: 4,
            color: '#7c3aed',
            weight: 1.5,
            fillColor: '#a78bfa',
            fillOpacity: 0.9,
          })
            .bindPopup(
              `<strong>${zone.code}</strong>${pt.label ? ` · ${pt.label}` : ''}` +
                (pt.accuracy ? `<br/>±${pt.accuracy} m` : ''),
            )
            .addTo(layer);
          bounds.push([pt.lat, pt.lng]);
        }
      }
    }

    // Points de variétés relevés au GSM (jaune, avec le nom de la variété)
    for (const cp of cropPoints || []) {
      const gps = cp.gps_coordinates;
      const pts = gps && Array.isArray(gps.points) ? gps.points : [];
      for (const pt of pts) {
        if (typeof pt?.lat !== 'number' || typeof pt?.lng !== 'number') continue;
        L.circleMarker([pt.lat, pt.lng], {
          radius: 5,
          color: '#78350f',
          weight: 1.5,
          fillColor: '#fbbf24',
          fillOpacity: 0.95,
        })
          .bindTooltip(pt.label || cp.variety_name, { direction: 'top', offset: [0, -4] })
          .bindPopup(
            `<strong>${pt.label || cp.variety_name}</strong><br/>${cp.variety_species} — planche ${cp.plot_code}` +
              (pt.accuracy ? `<br/>±${pt.accuracy} m` : ''),
          )
          .addTo(layer);
        bounds.push([pt.lat, pt.lng]);
      }
    }

    if (bounds.length > 0) {
      map.fitBounds(L.latLngBounds(bounds as L.LatLngExpression[]).pad(0.25), {
        maxZoom: 18,
      });
    }
  }, [zones, cropPoints]);

  const hasZoneGps = zones.some(
    (z) => z.gps_coordinates && typeof z.gps_coordinates === 'object' && (z.gps_coordinates.lat != null || z.gps_coordinates.polygon),
  );
  const hasCropGps = (cropPoints || []).some(
    (c) => c.gps_coordinates && Array.isArray(c.gps_coordinates.points) && c.gps_coordinates.points.length > 0,
  );

  if (!hasZoneGps && !hasCropGps) return null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div ref={containerRef} style={{ height: 420 }} />
      <p className="px-4 py-2 text-xs text-gray-500">
        🟢 zone relevée au GSM · 🟡 variété localisée (nom au survol) · 🟠 zone approximative
        (pointillés) — relevé depuis la PWA terrain → Options → <strong>Relevé GPS</strong>
      </p>
    </div>
  );
}
